import type { AnalyticsConfig, ConsentState } from "./types";
import type { AdpAnalytics } from "@adsk/adp-web-analytics-sdk";
import { isOAuthPath, PATHS, resolvePage } from "./routes";

/**
 * The only module that references `@adsk/adp-web-analytics-sdk`. The SDK is
 * loaded with a dynamic import() so that, with analytics disabled, its chunk is
 * never requested. Nothing here throws: every failure means "no tracking".
 */

let config: AnalyticsConfig | undefined;
let clientPromise: Promise<AdpAnalytics> | undefined;
let client: AdpAnalytics | undefined;
let userSession: Promise<ConsentState> | undefined;
let consent: ConsentState = "not-opted-in";

export function configureAnalytics(next: AnalyticsConfig): void {
    config = next;
}

/** Clears module state between tests. */
export function resetForTests(): void {
    config = undefined;
    clientPromise = undefined;
    client = undefined;
    userSession = undefined;
    consent = "not-opted-in";
}

function isConsoleFallback(): boolean {
    return !!config?.enabled && !config.productName;
}

/**
 * The SDK calls this for every event with `document.location.href`. It keeps only
 * the pathname and search of the returned URL, and needs an absolute URL (a
 * relative one throws inside the SDK, which then sends the original URL). The
 * pattern replaces every id, and the query string and hash are dropped.
 */
export function toPatternUrl(url: string): string {
    try {
        const { origin, pathname } = new URL(url);
        if (isOAuthPath(pathname)) {
            return `${origin}${PATHS.OAUTH}`;
        }
        return `${origin}${resolvePage(pathname).pattern ?? "/not-found"}`;
    } catch {
        return `${window.location.origin}/not-found`;
    }
}

/**
 * The SDK sets every event's `title` from `document.title`, which can hold an
 * organization, group, library or schema name. This facet filter drops
 * `page_title` and leaves every other field.
 */
export function titleFacetFilter(): { filtering: { page: { output: Record<string, string> }[] } } {
    return { filtering: { page: [{ output: { "!page_title": ".*" } }] } };
}

function initClient(current: AnalyticsConfig): Promise<AdpAnalytics> {
    if (!clientPromise) {
        clientPromise = import("@adsk/adp-web-analytics-sdk").then(({ Adp }) => {
            const adp = Adp();
            adp.setEnv(current.env)
                .disableAutoPageCapture()
                .setPageUrlHook(toPatternUrl)
                .setRestrictedSearchParameters(/.*/)
                .setFacetFilterHook(titleFacetFilter);
            if (!current.isProduction) {
                adp.enableDebug();
            }
            adp.load({
                name: current.productName,
                id: current.productId,
                id_provider: "appkey",
                ...(current.productLineName ? { line_name: current.productLineName } : {}),
            });
            client = adp;
            return adp;
        });
    }
    return clientPromise;
}

function consentFromPreferences(preferences: { optIn: boolean | null }[]): ConsentState {
    const answered = preferences.length > 0 && preferences.every(({ optIn }) => optIn !== null);
    return answered && preferences.some(({ optIn }) => optIn === true) ? "opted-in" : "not-opted-in";
}

async function resolveConsent(adp: AdpAnalytics, token: string): Promise<ConsentState> {
    const result = await adp.loadUserConsentPreferences(token);
    if (result instanceof Error || !("status" in result)) {
        return "not-opted-in";
    }
    const { status } = result;
    if (status.code === status.USER_OPT_IN) {
        return "opted-in";
    }
    if (status.code !== status.PENDING_USER_ACTION) {
        return "not-opted-in";
    }
    await adp.showUserConsentDialog();
    return consentFromPreferences(await adp.getCurrentUserConsentPreferences());
}

async function runUserSession(current: AnalyticsConfig, token: string): Promise<ConsentState> {
    if (isConsoleFallback()) {
        console.info("[analytics] not configured (no ADP_PRODUCT_NAME): treating consent as opted-in");
        return "opted-in";
    }
    const adp = await initClient(current);
    const state = await resolveConsent(adp, token);
    if (state === "opted-in") {
        adp.identify();
    }
    return state;
}

/**
 * Starts the named session: consent lookup (with the SDK dialog when the user
 * has not answered yet), then `identify()` once opted in. Idempotent per page
 * load and not keyed by token, so StrictMode double effects and token refreshes
 * cause one consent call and one identify().
 */
export function startUserSession(token: string): Promise<ConsentState> {
    if (!config?.enabled) {
        return Promise.resolve("not-opted-in");
    }
    if (!userSession) {
        userSession = runUserSession(config, token)
            .catch((): ConsentState => "not-opted-in")
            .then((state) => {
                consent = state;
                return state;
            });
    }
    return userSession;
}

export function sendPage(pageName: string, properties?: Record<string, unknown>): void {
    if (consent !== "opted-in") {
        return;
    }
    try {
        if (isConsoleFallback()) {
            console.info(`[analytics] page ${pageName}`, properties ?? "");
        } else {
            client?.page(pageName, properties);
        }
    } catch {
        // Analytics must never affect the app.
    }
}
